// Copyright (c) 2026. LodgIQ Inc, All rights reserved
//
// AI Insight panel — "Summarize this view".
//
// A self-contained strip that docks between the workspace controls and the
// (absolutely-positioned) chart/table containers. Design constraints from the
// impact analysis:
//   - Streaming text lives in LOCAL state only (never Redux) so token updates
//     re-render just this panel — DynamicTable/Chart are memoized siblings.
//   - The panel is a SIBLING of the drop-target boxes (never wraps them), so
//     tile-merge drag & drop is untouched.
//   - Space is reserved by adding `lq-insight-open` on #mychartview (parent
//     passes it) which shrinks the containers' calc() offsets; a window resize
//     event is dispatched so Highcharts reflows (it has no ResizeObserver).
//
// Opened via the window event "lq:view-insight" (fired by the WorkspaceControls
// trigger). Any deeper interaction hands off to Ask LodgIQ in mini mode via
// "lq:open-chat" with the insight's session id.

import React, { useEffect, useRef, useState } from "react";
import { useSelector, useStore } from "react-redux";
import { streamChat, sendFeedback, saveInsightDisplay, getInsightDisplay } from "../../services/wizard.service";
import { renderContent, contentToHtml } from "../Wizard/LodgiqWizard";
import "./InsightPanel.scss";

export function buildViewDescriptor(store) {
  const cur = (store.getState() || {}).app?.current_state || {};
  const appState = cur.app_state || {};
  const uiState = cur.ui_state || {};
  const c = appState.workspace_controls || {};
  const range = appState.primary_stay_range || {};
  const summary = appState.summary_stay_range || {};
  return {
    tiles: uiState.active_tiles || [],
    view_mode: uiState.workspace_view || "table",
    date_from: range.date_from,
    date_until: range.date_until,
    // Summary tiles (hotel/market monthly summary) display THIS range, not primary.
    summary_date_from: summary.date_from,
    summary_date_until: summary.date_until,
    as_of: range.as_of_date || appState.as_of_date,
    segment_type: c.segmentType || "hotel",
    segmented: !!c.segmented_view,
    scope: c.scopeType || "Date",
    sdly_on: !!c.sdlyOn,
    pickup_days: c.pickUpDays || 7,
    // Sensitivity Analysis is shaped by three more controls. Without them the
    // summary ran at the default 45 DBA and all seven days regardless of the
    // pickers, and against the default comparison window rather than the chosen one.
    dba: c.dba || null,
    dow: c.dayOfWeek || null,
    // RevPAR Performance has its own Monthly/Daily toggle. Without it the summary
    // guessed the grain from the window length and ignored the user's choice.
    table_type: c.tableType || null,
    comp_date_from: (appState.comp_stay_range || {}).date_from,
    comp_date_until: (appState.comp_stay_range || {}).date_until,
    // Pickup By Segment / Pace By Segment ignore workspace segmentType entirely and
    // use their OWN filter (ReportFilter.js): a category plus that category's top
    // segments. Without this the backend saw segmentType="hotel" and summarised a
    // segmented report at hotel total.
    report_filter: {
      category: [].concat(uiState.reports_dpbs?.selectedCategory || []),
      segments: [].concat(uiState.reports_dpbs?.selectedSegment || []),
    },
  };
}

const viewKey = (v) =>
  [v.tiles.join("-"), v.date_from, v.date_until, v.segment_type, v.segmented, v.scope,
   // every control that changes the numbers must bust the cached view
   v.dba, v.dow, v.table_type, v.comp_date_from, v.comp_date_until,
   (v.report_filter?.category || []).join(","),
   (v.report_filter?.segments || []).join(",")].join("|");

export default function InsightPanel({ onOpenChange }) {
  const store = useStore();
  const [open, setOpen] = useState(false);
  const [text, setText] = useState("");
  const [phase, setPhase] = useState("idle"); // idle | loading | streaming | done | error
  const [error, setError] = useState("");
  const [stale, setStale] = useState(false);
  const [messageId, setMessageId] = useState(null); // DB id -> enables feedback
  const [copied, setCopied] = useState(false);
  const [vote, setVote] = useState("");
  const [showPrefMenu, setShowPrefMenu] = useState(false);
  const sessionRef = useRef(null);
  const abortRef = useRef(null);
  const snapshotRef = useRef("");
  const bodyRef = useRef(null);
  const [topPx, setTopPx] = useState(null); // measured: flush under the real controls row

  // The CSS top offsets are the containers' reserved band (111/140px), but the
  // rendered controls are often shorter — measure them and dock right below,
  // so there's no dead gap above the panel.
  const measureTop = () => {
    try {
      const root = document.getElementById("mychartview");
      const controls = root && root.querySelector(".work-space.controls");
      if (root && controls) {
        const t = controls.getBoundingClientRect().bottom - root.getBoundingClientRect().top;
        if (t > 40 && t < 300) { setTopPx(Math.round(t) + 2); return; }
      }
    } catch (_) {}
    setTopPx(null); // fall back to the CSS offsets
  };

  const propertyId = useSelector((s) => s.app?.current_state?.app_state?.property_id);
  const loginInsightPref = useSelector((s) => s.auth?.user?.insight_display);
  // Re-render on view changes only to detect staleness (cheap string compare).
  const liveKey = useSelector((s) => {
    const cur = s.app?.current_state || {};
    const u = cur.ui_state || {}, a = cur.app_state || {}, c = a.workspace_controls || {};
    const r = a.primary_stay_range || {};
    const rf = u.reports_dpbs || {};
    const cr = a.comp_stay_range || {};
    return [(u.active_tiles || []).join("-"), r.date_from, r.date_until, c.segmentType,
            !!c.segmented_view, c.scopeType,
            c.dba, c.dayOfWeek, c.tableType, cr.date_from, cr.date_until,
            // report tiles carry their own filter — changing it makes the summary stale
            [].concat(rf.selectedCategory || []).join(","),
            [].concat(rf.selectedSegment || []).join(",")].join("|");
  });

  const notifyOpen = (v) => {
    setOpen(v);
    onOpenChange && onOpenChange(v);
    if (v) measureTop();
    // Highcharts has no ResizeObserver; it does listen to window resize.
    setTimeout(() => { try { window.dispatchEvent(new Event("resize")); } catch (_) {} }, 80);
  };

  // Keep the measured offset honest across window resizes / control changes.
  useEffect(() => {
    if (!open) return undefined;
    const onResize = () => measureTop();
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [open]);

  const run = () => {
    if (abortRef.current) abortRef.current.abort();
    const view = buildViewDescriptor(store);
    if (!view.tiles.length || !propertyId) return;
    snapshotRef.current = viewKey(view);
    setStale(false);
    setText("");
    setError("");
    setMessageId(null);
    setVote("");
    setCopied(false);
    setPhase("loading");
    notifyOpen(true);

    const controller = new AbortController();
    abortRef.current = controller;
    let acc = "";
    streamChat({
      propertyId,
      view,
      intent: "summarize",
      sessionId: sessionRef.current,
      signal: controller.signal,
      onDelta: (d) => {
        acc += d;
        setText(acc);
        setPhase("streaming");
        if (bodyRef.current) bodyRef.current.scrollTop = bodyRef.current.scrollHeight;
      },
      onDone: (sid, msgId) => {
        if (sid) sessionRef.current = sid;
        if (msgId) setMessageId(msgId);
        setPhase("done");
      },
      onError: (msg) => {
        setError(msg || "Something went wrong. Please try again.");
        setPhase("error");
      },
    });
  };

  const close = () => {
    if (abortRef.current) abortRef.current.abort();
    setPhase("idle");
    notifyOpen(false);
  };

  // Rich copy (HTML + plain), same behavior as the chat's copy button.
  const copyInsight = () => {
    if (!text) return;
    const done = () => { setCopied(true); setTimeout(() => setCopied(false), 1500); };
    try {
      if (window.ClipboardItem && navigator.clipboard && navigator.clipboard.write) {
        const item = new window.ClipboardItem({
          "text/html": new Blob([contentToHtml(text)], { type: "text/html" }),
          "text/plain": new Blob([text], { type: "text/plain" }),
        });
        navigator.clipboard.write([item]).then(done).catch(() => {
          navigator.clipboard.writeText(text).then(done).catch(() => {});
        });
        return;
      }
      navigator.clipboard.writeText(text).then(done).catch(() => {});
    } catch (_) {}
  };

  // Thumbs — same API as chat feedback; toggle clears the vote.
  const voteInsight = (dir) => {
    if (!messageId || !propertyId) return;
    const next = vote === dir ? "" : dir;
    const prev = vote;
    setVote(next);
    sendFeedback(propertyId, messageId, next).catch(() => setVote(prev));
  };

  const continueInChat = () => {
    try {
      window.dispatchEvent(new CustomEvent("lq:open-chat", { detail: { sessionId: sessionRef.current } }));
    } catch (_) {}
    close();
  };

  // In-context preference switcher (same value the Settings page saves).
  const setDisplayPref = (opt) => {
    setShowPrefMenu(false);
    saveInsightDisplay(opt);
    if (opt === "chat") continueInChat(); // take effect immediately: move this insight to the chat
  };

  // Trigger: fired by the WorkspaceControls button.
  useEffect(() => {
    const onTrigger = () => run();
    window.addEventListener("lq:view-insight", onTrigger);
    return () => window.removeEventListener("lq:view-insight", onTrigger);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [propertyId]);

  // Property switch: drop everything (different hotel, different session).
  useEffect(() => {
    sessionRef.current = null;
    if (open) close();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [propertyId]);

  // View changed while an insight is showing -> offer refresh instead of lying.
  useEffect(() => {
    if (open && snapshotRef.current && liveKey !== snapshotRef.current) setStale(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [liveKey]);

  if (!open) return null;

  return (
    <div
      className="lq-insight"
      style={topPx != null ? { top: topPx } : undefined}
      role="complementary"
      aria-label="AI insight for this view"
    >
      <div className="lq-insight__head">
        <span className="lq-insight__badge">AI insight</span>
        {stale && (
          <button className="lq-insight__stale" onClick={run}>
            View changed · Refresh
          </button>
        )}
        <span className="lq-insight__spacer" />
        <button className="lq-insight__act" onClick={run} title="Refresh insight">⟳</button>
        <button className="lq-insight__act lq-insight__act--chat" onClick={continueInChat} title="Continue in Ask LodgIQ">
          Ask a follow-up ↗
        </button>
        <span className="lq-insight__prefwrap">
          <button
            className={`lq-insight__act ${showPrefMenu ? "is-on" : ""}`}
            onClick={() => setShowPrefMenu((v) => !v)}
            title="Insight display settings"
          >
            ⚙
          </button>
          {showPrefMenu && (
            <span className="lq-insight__prefmenu">
              <span className="lq-insight__prefhead">Insight display</span>
              <button
                className={getInsightDisplay(loginInsightPref) === "inline" ? "is-sel" : ""}
                onClick={() => setDisplayPref("inline")}
              >
                Inline panel over the view
              </button>
              <button
                className={getInsightDisplay(loginInsightPref) === "chat" ? "is-sel" : ""}
                onClick={() => setDisplayPref("chat")}
              >
                Ask LodgIQ chat window
              </button>
              <span className="lq-insight__preffoot">Saved to your profile</span>
            </span>
          )}
        </span>
        <button className="lq-insight__act" onClick={close} title="Close">✕</button>
      </div>
      <div className="lq-insight__body" ref={bodyRef}>
        {phase === "loading" && <span className="lq-insight__wait">Reading this view…</span>}
        {phase === "error" ? <span className="lq-insight__err">{error}</span> : renderContent(text)}
      </div>
      <div className="lq-insight__foot">
        <span className="lq-insight__foot-note">Ask LodgIQ may make mistakes. Verify responses.</span>
        <span className="lq-insight__foot-actions">
          <button
            className={`lq-insight__mini-act ${copied ? "is-on" : ""}`}
            onClick={copyInsight}
            disabled={!text}
            title={copied ? "Copied" : "Copy insight"}
          >
            {copied ? (
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12l5 5L20 7" /></svg>
            ) : (
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="9" y="9" width="11" height="11" rx="2" /><path d="M5 15V5a2 2 0 0 1 2-2h10" /></svg>
            )}
            <span>{copied ? "Copied" : "Copy"}</span>
          </button>
          <button
            className={`lq-insight__mini-act ${vote === "up" ? "is-on is-up" : ""}`}
            onClick={() => voteInsight("up")}
            disabled={!messageId}
            title="Good insight"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M7 10v11H4a1 1 0 0 1-1-1v-9a1 1 0 0 1 1-1h3zM7 10l4.2-7a2.3 2.3 0 0 1 2.3 2.3V9h5.1a2 2 0 0 1 2 2.4l-1.4 7a2 2 0 0 1-2 1.6H7" /></svg>
          </button>
          <button
            className={`lq-insight__mini-act ${vote === "down" ? "is-on is-down" : ""}`}
            onClick={() => voteInsight("down")}
            disabled={!messageId}
            title="Poor insight"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 14V3h3a1 1 0 0 1 1 1v9a1 1 0 0 1-1 1h-3zM17 14l-4.2 7a2.3 2.3 0 0 1-2.3-2.3V15H5.4a2 2 0 0 1-2-2.4l1.4-7a2 2 0 0 1 2-1.6H17" /></svg>
          </button>
        </span>
      </div>
    </div>
  );
}
